import { deepMerge } from '@antfu/utils'
import { useI18n } from 'vue-i18n'
import { createVuetify } from 'vuetify'
import { VBtn } from 'vuetify/components/VBtn'
import { createVueI18nAdapter } from 'vuetify/locale/adapters/vue-i18n'
import defaults from './defaults'
import { icons } from './icons'
import {
  staticPrimaryColor,
  staticPrimaryDarkenColor,
  staticSecondaryColor,
  staticSageColor,
  themes,
} from './theme'
import { getI18n } from '@/plugins/i18n/index'
import { themeConfig } from '@themeConfig'
import { cookieRef } from '@/@layouts/stores/config'

// Styles
import '@core/scss/template/libs/vuetify/index.scss'
import 'vuetify/styles'

/**
 * Resolves Vuetify theme name to Salubris theme equivalent
 * @param {string} themeName - Original theme name
 * @returns {string} - Salubris theme name
 */
function resolveSalubrisTheme(themeName) {
  const themeMap = {
    light: 'salubrisLight',
    dark: 'salubrisDark',
    salubrisLight: 'salubrisLight',
    salubrisDark: 'salubrisDark',
  }

  // Default to dark theme
  return themeMap[themeName] || 'salubrisDark'
}

/**
 * Legacy theme resolver for backward compatibility
 * @param {string} themeName - Theme name from config
 * @returns {string} - Resolved theme name
 */
function resolveVuetifyTheme(themeName) {
  // Support both old and new theme naming
  if (themeName === 'dark') return 'salubrisDark'
  if (themeName === 'light') return 'salubrisLight'

  // Default to dark theme for undefined/empty values
  if (!themeName) return 'salubrisDark'

  return resolveSalubrisTheme(themeName)
}

export default function setupVuetify(app) {
  // Enhanced cookie theme values with Salubris branding - Dark as default
  const cookieThemeValues = {
    defaultTheme: resolveVuetifyTheme(themeConfig.app.theme) || 'salubrisDark',
    themes: {
      // Salubris Dark Theme with Cookie Overrides (Primary theme)
      salubrisDark: {
        colors: {
          // Primary Eucalyptus Colors (Lightened for dark)
          primary: cookieRef('salubrisDarkThemePrimaryColor', '#8FA67A').value, // Light eucalyptus
          'primary-darken-1': cookieRef(
            'salubrisDarkThemePrimaryDarkenColor',
            staticPrimaryColor
          ).value,

          // Secondary Terra Colors (Lightened for dark)
          secondary: cookieRef('salubrisDarkThemeSecondaryColor', '#C2956F')
            .value, // Light terra

          // Tertiary Sage Colors (Lightened for dark)
          accent: cookieRef('salubrisDarkThemeSageColor', '#B0C4C0').value, // Light sage

          // Application-specific customizable colors
          'sharepoint-primary': cookieRef(
            'salubrisDarkSharePointPrimary',
            '#8FA67A'
          ).value,
          'weather-accent': cookieRef('salubrisDarkWeatherAccent', '#B0C4C0')
            .value,
          'asset-primary': cookieRef('salubrisDarkAssetPrimary', '#8FA67A')
            .value,
        },
      },

      // Salubris Light Theme with Cookie Overrides (Secondary theme)
      salubrisLight: {
        colors: {
          // Primary Eucalyptus Colors
          primary: cookieRef(
            'salubrisLightThemePrimaryColor',
            staticPrimaryColor
          ).value,
          'primary-darken-1': cookieRef(
            'salubrisLightThemePrimaryDarkenColor',
            staticPrimaryDarkenColor
          ).value,

          // Secondary Terra Colors (Traditional Owner Heritage)
          secondary: cookieRef(
            'salubrisLightThemeSecondaryColor',
            staticSecondaryColor
          ).value,

          // Tertiary Sage Colors
          accent: cookieRef('salubrisLightThemeSageColor', staticSageColor)
            .value,

          // Application-specific customizable colors
          'sharepoint-primary': cookieRef(
            'salubrisSharePointPrimary',
            staticPrimaryColor
          ).value,
          'weather-accent': cookieRef('salubrisWeatherAccent', staticSageColor)
            .value,
          'asset-primary': cookieRef('salubrisAssetPrimary', staticPrimaryColor)
            .value,
        },
      },

      // Legacy theme support for backward compatibility - dark as primary
      dark: {
        colors: {
          primary: cookieRef('darkThemePrimaryColor', '#8FA67A').value, // Light eucalyptus for dark
          'primary-darken-1': cookieRef(
            'darkThemePrimaryDarkenColor',
            staticPrimaryColor
          ).value,

          // Map to Salubris colors
          secondary: cookieRef('darkThemeSecondaryColor', '#C2956F').value, // Light terra for dark
          accent: cookieRef('darkThemeSageColor', '#B0C4C0').value, // Light sage for dark
        },
      },

      light: {
        colors: {
          primary: cookieRef('lightThemePrimaryColor', staticPrimaryColor)
            .value,
          'primary-darken-1': cookieRef(
            'lightThemePrimaryDarkenColor',
            staticPrimaryDarkenColor
          ).value,

          // Map to Salubris colors
          secondary: cookieRef('lightThemeSecondaryColor', staticSecondaryColor)
            .value,
          accent: cookieRef('lightThemeSageColor', staticSageColor).value,
        },
      },
    },
  }

  // Merge Salubris themes with cookie customizations
  const optionTheme = deepMerge({ themes }, cookieThemeValues)

  // Create Vuetify instance with Salubris configuration
  const vuetify = createVuetify({
    aliases: {
      IconBtn: VBtn,
    },
    defaults: {
      ...defaults,
      // Enhanced defaults for Salubris components
      VBtn: {
        ...defaults?.VBtn,
        // Apply natural motion curves
        style: 'transition: all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);',
      },
      VCard: {
        ...defaults?.VCard,
        // Natural elevation shadows
        elevation: 2,
      },
      VTextField: {
        ...defaults?.VTextField,
        // Eucalyptus-inspired focus colors
        variant: 'outlined',
      },
    },
    icons,
    theme: {
      ...optionTheme,
      // Add custom CSS properties for design tokens
      cssVariables: true,
      // Enable theme variations
      variations: {
        colors: [
          'primary',
          'secondary',
          'accent',
          'success',
          'warning',
          'error',
          'info',
        ],
        lighten: 5,
        darken: 5,
      },
    },
    locale: {
      adapter: createVueI18nAdapter({ i18n: getI18n(), useI18n }),
    },
    // Add Salubris-specific display options
    display: {
      mobileBreakpoint: 'sm',
      thresholds: {
        xs: 0,
        sm: 600,
        md: 960,
        lg: 1280,
        xl: 1920,
      },
    },
  })

  // Ensure Vuetify is only applied once
  if (!app._vuetify) {
    app.use(vuetify)
    app._vuetify = vuetify // Custom flag to prevent re-initialization

    // Add Salubris design tokens to document root - optimized for dark theme
    if (typeof document !== 'undefined') {
      const root = document.documentElement

      // Set Salubris CSS custom properties - dark theme optimized
      root.style.setProperty(
        '--salubris-eucalyptus-primary',
        '#8FA67A' // Light eucalyptus for dark default
      )
      root.style.setProperty(
        '--salubris-eucalyptus-darken',
        staticPrimaryColor // Original as darker variant
      )
      root.style.setProperty('--salubris-terra-secondary', '#C2956F') // Light terra for dark
      root.style.setProperty('--salubris-sage-accent', '#B0C4C0') // Light sage for dark

      // Set motion design tokens
      root.style.setProperty(
        '--motion-eucalyptus',
        'cubic-bezier(0.25, 0.46, 0.45, 0.94)'
      )
      root.style.setProperty(
        '--motion-breeze',
        'cubic-bezier(0.23, 1, 0.32, 1)'
      )
      root.style.setProperty(
        '--motion-earth',
        'cubic-bezier(0.55, 0.06, 0.68, 0.19)'
      )

      // Set semantic color tokens - dark theme optimized
      root.style.setProperty('--semantic-success', '#66956D') // Lightened for dark
      root.style.setProperty('--semantic-warning', '#E1B88A') // Lightened for dark
      root.style.setProperty('--semantic-error', '#D47A61') // Lightened for dark
      root.style.setProperty('--semantic-info', '#7AA6B8') // Lightened for dark

      // Application-specific tokens - dark theme optimized
      root.style.setProperty('--sharepoint-integration', '#8FA67A') // Light eucalyptus
      root.style.setProperty('--weather-primary', '#7AA6B8') // Light ocean blue
      root.style.setProperty('--asset-tracking', '#8FA67A') // Light eucalyptus

      // Cultural heritage tokens - dark theme optimized
      root.style.setProperty(
        '--traditional-owner-heritage',
        '#C2956F' // Light terra for dark
      )
      root.style.setProperty('--western-australian-flora', '#8FA67A') // Light eucalyptus
    }
  }

  return vuetify
}

// Export additional Salubris utilities
export const salubrisUtils = {
  /**
   * Get current Salubris theme colors
   * @param {boolean} isDark - Whether dark theme is active (defaults to true now)
   * @returns {object} - Theme colors object
   */
  getThemeColors(isDark = true) {
    // Changed default to true for dark theme
    const theme = isDark ? themes.salubrisDark : themes.salubrisLight
    return theme.colors
  },

  /**
   * Apply Salubris motion curve to element
   * @param {string} curve - Motion curve name ('eucalyptus', 'breeze', 'earth')
   * @returns {string} - CSS transition value
   */
  getMotionCurve(curve = 'eucalyptus') {
    const curves = {
      eucalyptus: 'cubic-bezier(0.25, 0.46, 0.45, 0.94)',
      breeze: 'cubic-bezier(0.23, 1, 0.32, 1)',
      earth: 'cubic-bezier(0.55, 0.06, 0.68, 0.19)',
    }
    return curves[curve] || curves.eucalyptus
  },

  /**
   * Get application-specific color (optimized for dark theme default)
   * @param {string} app - Application name ('sharepoint', 'weather', 'asset')
   * @param {string} variant - Color variant ('primary', 'secondary', 'accent')
   * @param {boolean} isDark - Whether to get dark theme colors (defaults to true)
   * @returns {string} - Color value
   */
  getAppColor(app, variant = 'primary', isDark = true) {
    // Changed default to true
    const lightAppColors = {
      sharepoint: {
        primary: staticPrimaryColor,
        secondary: staticSecondaryColor,
        accent: staticSageColor,
      },
      weather: {
        primary: '#5B8FA3', // Southern Ocean blue
        secondary: '#D4A574', // Golden wattle
        accent: '#8FA8A3', // Sage neutral
      },
      asset: {
        primary: staticPrimaryColor,
        secondary: '#4A7C59', // Success green
        accent: '#C65D42', // Alert red
      },
    }

    const darkAppColors = {
      sharepoint: {
        primary: '#8FA67A', // Light eucalyptus for dark
        secondary: '#C2956F', // Light terra for dark
        accent: '#B0C4C0', // Light sage for dark
      },
      weather: {
        primary: '#7AA6B8', // Light coastal sky
        secondary: '#E1B88A', // Light golden amber
        accent: '#B0C4C0', // Light sage
      },
      asset: {
        primary: '#8FA67A', // Light eucalyptus for dark
        secondary: '#66956D', // Light success green
        accent: '#D47A61', // Light alert red
      },
    }

    const appColors = isDark ? darkAppColors : lightAppColors
    return (
      appColors[app]?.[variant] || (isDark ? '#8FA67A' : staticPrimaryColor)
    )
  },

  /**
   * Check if color meets WCAG contrast requirements
   * @param {string} _foreground - Foreground color (unused - placeholder for future integration)
   * @param {string} _background - Background color (unused - placeholder for future integration)
   * @returns {object} - Contrast compliance info
   */
  // eslint-disable-next-line no-unused-vars
  checkContrast(_foreground, _background) {
    // This would integrate with a contrast checking library
    // For now, return mock data based on our tested values
    // TODO: Implement actual contrast calculation using foreground and background
    return {
      ratio: 4.87, // Example ratio for eucalyptus on dark background
      aa: true,
      aaa: true,
      large: true,
    }
  },
}

export { resolveSalubrisTheme, resolveVuetifyTheme }
